import React, { useState } from 'react';
import { FiClock } from 'react-icons/fi';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import type { ChannelDashboardOverview, ChannelMediaAsset, ChannelMediaType } from '@troia-v3/types';

import { useCreateChannelMedia, usePublishChannelMedia } from '../../hooks';
import { getProviderLabel } from '../../utils/getProviderLabel';
import { MediaDropzone } from './MediaDropzone';
import { PhonePreview } from './PhonePreview';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

const TYPES: Array<{ key: ChannelMediaType; label: string }> = [
  { key: 'feed', label: 'Feed' },
  { key: 'carousel', label: 'Carrossel' },
  { key: 'reels', label: 'Reels' },
  { key: 'story', label: 'Story' },
];

const FB_TYPES: ChannelMediaType[] = ['feed', 'carousel'];

interface Props {
  channelId: string;
  channel: ChannelDashboardOverview['channel'];
}

export const PostComposer: React.FC<Props> = ({ channelId, channel }) => {
  const confirm = useConfirm();
  const [mediaType, setMediaType] = useState<ChannelMediaType>('feed');
  const [assets, setAssets] = useState<ChannelMediaAsset[]>([]);
  const [caption, setCaption] = useState('');
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');

  const createMutation = useCreateChannelMedia(channelId);
  const providerLabel = getProviderLabel(channel.providerId);
  const publishMutation = usePublishChannelMedia(channelId, providerLabel);
  const busy = createMutation.isPending || publishMutation.isPending;

  const isFacebook = providerLabel === 'Facebook';
  const availableTypes = isFacebook ? TYPES.filter((t) => FB_TYPES.includes(t.key)) : TYPES;

  const reset = (): void => {
    setAssets([]);
    setCaption('');
    setScheduleOn(false);
    setScheduledAt('');
  };

  const changeType = (t: ChannelMediaType): void => {
    setMediaType(t);
    if (t !== 'carousel' && assets.length > 1) {
      setAssets(assets.slice(0, 1).map((a, i) => ({ ...a, order: i })));
    }
  };

  const validate = (): boolean => {
    if (assets.length === 0) {
      toast.error('Adicione ao menos uma mídia');
      return false;
    }
    return true;
  };

  const captionField = caption.trim() ? { caption: caption.trim() } : {};

  const saveDraft = async (): Promise<void> => {
    if (!validate()) return;
    try {
      await createMutation.mutateAsync({ mediaType, assets, status: 'draft', ...captionField });
      toast.success('Rascunho salvo');
      reset();
    } catch {
      /* toast de erro já emitido pelo onError da mutation; mantém o composer preenchido */
    }
  };

  const schedule = async (): Promise<void> => {
    if (!validate()) return;
    if (!scheduledAt) {
      toast.error('Escolha data e hora do agendamento');
      return;
    }
    const iso = new Date(scheduledAt).toISOString();
    if (new Date(iso).getTime() <= Date.now()) {
      toast.error('O agendamento deve ser no futuro');
      return;
    }
    try {
      await createMutation.mutateAsync({ mediaType, assets, status: 'scheduled', scheduledAt: iso, ...captionField });
      toast.success('Publicação agendada');
      reset();
    } catch {
      /* onError da mutation já avisou */
    }
  };

  const publishNow = async (): Promise<void> => {
    if (!validate()) return;
    if (!(await confirm({ message: `Publicar agora no ${providerLabel}? Esta ação não pode ser desfeita.`, confirmButtonText: 'Publicar agora', variant: 'default' }))) return;
    let createdId: string;
    try {
      const created = await createMutation.mutateAsync({ mediaType, assets, status: 'draft', ...captionField });
      createdId = created.id;
    } catch {
      return; // create falhou — mantém o composer (nenhum registro criado)
    }
    // Draft criado: o registro já existe na fila. Publica; em qualquer desfecho, limpa o
    // composer (sucesso publica; falha deixa um item 'failed' retomável na fila — evita duplicar no re-clique).
    try {
      await publishMutation.mutateAsync(createdId);
    } catch {
      /* onError da mutation já avisou; item ficou 'failed' na fila */
    } finally {
      reset();
    }
  };

  return (
    <div data-testid="cdash-composer">
      <span className="cdash-mlabel">Nova publicação</span>
      <div className="cdash-composer">
        <div>
          <div className="cdash-seg" data-testid="cdash-type-seg">
            {availableTypes.map((t) => (
              <button
                key={t.key}
                className="cdash-seg-item"
                aria-pressed={mediaType === t.key}
                onClick={() => changeType(t.key)}
                data-testid={`cdash-type-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <MediaDropzone assets={assets} onChange={setAssets} multiple={mediaType === 'carousel'} />

          <div style={{ marginTop: 14 }}>
            <textarea
              className="cdash-ta"
              placeholder="Escreva uma legenda…"
              value={caption}
              maxLength={2200}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="cdash-caption"
            />
          </div>

          <label className="cdash-toggle-row">
            <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} data-testid="cdash-schedule-toggle" />
            <FiClock size={14} /> Agendar
          </label>
          {scheduleOn && (
            <div style={{ marginTop: 10 }}>
              <input
                type="datetime-local"
                className="cdash-datetime"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="cdash-schedule-at"
              />
            </div>
          )}

          {/* Ações — Button shared (fora do CSS isolado cdash, F07/F08) */}
          <div className="cdash-composer-actions">
            <Button variant="secondary" size="md" icon="file-text" onClick={() => void saveDraft()} disabled={busy} data-testid="cdash-save-draft">
              Rascunho
            </Button>
            {scheduleOn ? (
              <Button variant="primary" size="md" icon="clock" onClick={() => void schedule()} disabled={busy} data-testid="cdash-schedule">
                Agendar
              </Button>
            ) : (
              <Button variant="primary" size="md" icon="send" onClick={() => void publishNow()} disabled={busy} data-testid="cdash-publish-now">
                Publicar agora
              </Button>
            )}
          </div>
        </div>

        <PhonePreview channel={channel} mediaType={mediaType} assets={assets} caption={caption} />
      </div>
    </div>
  );
};
